import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/space-grotesk/latin-600.css';
import './styles/tokens.css';
import './styles/global.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { createQueryClient, setUnauthenticatedHandler } from './api/query-client.js';
import { isPreAccountPath, LOGIN_PATH, returnToFor } from './auth/return-to.js';
import { publishBreakpointToCss } from './frame/breakpoints.js';
import { routeTree } from './routeTree.gen';

/** TanStack Router and TanStack Query (`patterns/frontend-conventions.md`, 1). */
const queryClient = createQueryClient();

// Back and Forward restore the scroll position a page was left at, so a list
// returned to is where it was (`patterns/ui-patterns.md`, 5).
const router = createRouter({ routeTree, context: { queryClient }, scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Any request refused with `UNAUTHENTICATED` goes to the log in screen with the
 * URL that was open as the return URL (`ux.md`, Flow B1 and B4). The log in
 * screen and the other pre-account pages run the same check themselves, so a
 * refusal there is already the state they show and needs no navigation.
 */
setUnauthenticatedHandler(() => {
  if (isPreAccountPath(window.location.pathname)) return;

  const returnTo = returnToFor(`${window.location.pathname}${window.location.search}`);
  void router.navigate({
    to: LOGIN_PATH,
    search: returnTo === null ? {} : { returnTo },
    replace: true,
  });
});

/**
 * A page restored from the browser's back-forward cache did not re-run
 * anything, so the session check runs again before its data is trusted
 * (`technical.md`, "No stale account data"). After log out that check is
 * refused, which sends the Account User to the log in screen.
 */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) void queryClient.invalidateQueries();
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('The dashboard needs an element with id "root" to render into.');
}

// The design system's `mobile` breakpoint is written in one place and published
// to CSS from there (`patterns/frontend-conventions.md`, 1).
publishBreakpointToCss(document.documentElement);

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
